<!-- ── Step 1: Account Deletion Confirmation ────────────────────────── -->
    @if (step() === 'confirm') {
      <p-dialog
        [visible]="true"
        [modal]="true"
        [closable]="false"
        [draggable]="false"
        [resizable]="false"
        styleClass="pra-deletion-dialog"
      >
        <ng-template pTemplate="header">
          <span class="text-[28px] font-bold text-gray-900">Account Deletion</span>
          <button (click)="close()"  class="ml-auto" aria-label="Close">
              <img src="assets/icons/add-collab.svg" class="rotate-45 h-[30px] w-[30px]">
            </button>
        </ng-template>

        <div class="px-[24px] pb-2">
          <!-- User info -->
          <div class="flex items-center gap-3 mb-5">
            <div class="relative w-[65px] h-[65px] rounded-full overflow-hidden border-2 border-gray-200 shrink-0">
              <img
                [src]="user()?.profile_picture || 'assets/icons/fill-icons/profile.png'"
                alt="Profile"
                [class.blur-sm]="isPictureBlocked(user()?.profile_picture_moderation_status)"
                class="w-full h-full object-cover"
              />
              @if (isPictureBlocked(user()?.profile_picture_moderation_status)) {
                <img src="assets/icons/eye-icon-white.png" alt="Under review" class="absolute inset-0 m-auto pointer-events-none" style="width: 20px; height: 20px;" />
              }
            </div>
            <div class="flex flex-col gap-0.5">
              <p class="text-[20px] font-bold text-heading m-0">{{ (user()?.first_name ?? '') + ' ' + (user()?.last_name ?? '') }}</p>
              <p class="text-base font-medium text-[#2F3A48] m-0">{{ user()?.email }}</p>
              <p class="text-sm font-medium text-[#2F3A48] m-0">{{ user()?.mobile }}</p>
            </div>
          </div>

          <h3 class="text-[22px] font-bold text-heading mb-2.5 mt-0">Account Deletion Confirmation</h3>
          <p class="text-lg text-heading leading-[20px] mb-3 mt-0">
            By proceeding, your account and personal data will be permanently deleted.
            <strong class="text-heading">This action is final and cannot be undone.</strong>
          </p>

          <p class="text-lg text-heading leading-[20px] mb-2 mt-0">
            Some information may remain due to legal or operational requirements, including:
          </p>
          <ul class="pl-4 mb-3 mt-0">
            <li class="text-lg text-heading leading-[20px]">Accounting and payment records</li>
            <li class="text-lg text-heading leading-[20px]">Linked transaction history</li>
          </ul>

          <p class="text-lg text-heading leading-[20px] mb-[40px] mt-0">
            <strong class="text-heading">By proceeding, a one-time password will be sent</strong>
            to the phone number or email associated with this account.
          </p>
        </div>
        <div class="border border-[#EBEFF2]"></div>
        <ng-template pTemplate="footer" class="px-[24px]">
          <div class="flex items-center justify-between gap-3 pt-1">
            <button
              (click)="close()"
              class="text-[20px] font-semibold text-heading bg-transparent border-none cursor-pointer px-2  transition-colors"
            >
              Cancel
            </button>
            <button
              (click)="onConfirmDeletion()"
              [disabled]="loading()"
              class="bg-[#C34137] disabled:bg-gray-400 disabled:cursor-not-allowed text-white border-none rounded-[5px] px-[24px] py-[5px] text-base font-semibold cursor-pointer transition-colors"
            >
              {{ loading() ? 'Sending...' : 'Confirm Deletion' }}
            </button>
          </div>
        </ng-template>
      </p-dialog>
    }

    <!-- ── Step 2: OTP Verification ─────────────────────────────────────── -->
    @if (step() === 'otp' || step() === 'action') {
      <p-dialog
        [visible]="true"
        [modal]="step() === 'otp'"
        [closable]="false"
        [draggable]="false"
        [resizable]="false"
        [styleClass]="step() === 'action' ? 'pra-deletion-dialog pra-deletion-dialog--bg' : 'pra-deletion-dialog'"
      >
        <ng-template pTemplate="header" >
          <span class="text-[28px] font-bold text-heading">OTP Verification</span>
          @if (step() === 'otp') {
            <button (click)="close()" class="ml-auto" aria-label="Close">
              <img src="assets/icons/add-collab.svg" class="rotate-45 h-[30px] w-[30px]">
            </button>
          }
        </ng-template>

        <div class="px-[24px] pb-2">
          <h3 class="text-[22px] font-bold text-heading mb-2.5 mt-0">Account Deletion Confirmation</h3>
          <p class="text-lg text-heading leading-[20px] mb-6 mt-0">
            Please enter the one-time password sent to the phone number or email associated with
            this account. <strong class="text-heading">This action is final and cannot be undone.</strong>
          </p>

          <!-- OTP input -->
          <div class="flex justify-center mb-2">
            <input
              type="text"
              [value]="otpDisplayValue()"
              (input)="onOtpInput($event)"
              placeholder="0 0 0 0"
              maxlength="7"
              inputmode="numeric"
              autofocus
              class="w-[160px] h-[52px] border rounded-[4px] bg-[#F9F9FA] text-center text-[24px] font-semibold text-heading outline-none placeholder:text-[#868889]"
              [class.border-[#C34137]]="otpError()"
              [class.border-[#EBEFF2]]="!otpError()"
              style="letter-spacing: 0px; padding: 0 20px;"
              (keyup.enter)="onVerifyOtp()"
            />
          </div>

          @if (otpError()) {
            <p class="text-[20px] font-semibold text-[#C34137] text-center mt-1 mb-0">Incorrect OTP</p>
          }

          <div class="flex flex-col items-center gap-0.5 mt-3">
            @if (resendCountdown() > 0) {
              <span class="text-[20px] text-[#8C929A]">Resend OTP</span>
              <span class="text-[20px] text-heading">{{ formatCountdown(resendCountdown()) }}</span>
            } @else {
              <button
                (click)="onResendOtp()"
                class="text-[20px] font-semibold text-[#238EC6] bg-transparent border-none cursor-pointer p-0 hover:underline"
              >
                Resend OTP
              </button>
            }
          </div>
        </div>
        <div class="border border-[#EBEFF2]"></div>
        <ng-template pTemplate="footer" class="px-[24px]">
          <div class="flex items-center justify-between gap-3 pt-1">
            <button
              (click)="close()"
              class="text-[20px] font-semibold text-heading bg-transparent border-none cursor-pointer px-2 hover:text-gray-700 transition-colors"
            >
              Cancel
            </button>
            <button
              (click)="onVerifyOtp()"
              [disabled]="!isOtpComplete() || loading()"
              class="bg-[#C34137] disabled:bg-gray-400 disabled:cursor-not-allowed text-white border-none rounded-[5px] px-[24px] py-[5px] text-base font-semibold cursor-pointer transition-colors"
            >
              {{ loading() ? 'Verifying...' : 'Confirm Deletion' }}
            </button>
          </div>
        </ng-template>
      </p-dialog>
    }

    <!-- ── Step 3: Confirm Action ────────────────────────────────────────── -->
    @if (step() === 'action') {
      <!-- Confirm Action dialog -->
      <p-dialog
        [visible]="true"
        [modal]="true"
        [closable]="false"
        [draggable]="false"
        [resizable]="false"
        styleClass="pra-confirm-action-dialog"
      >
        <ng-template pTemplate="header">
          <span class="text-[28px] font-bold text-heading">Confirm Action</span>
         <button (click)="close()" class="ml-auto"  aria-label="Close">
              <img src="assets/icons/add-collab.svg" class="rotate-45 h-[30px] w-[30px]">
            </button>
        </ng-template>

        <div class="px-[24px] py-4">
          <div class="flex items-start gap-3">
            <div
              (click)="confirmChecked = !confirmChecked"
              class="shrink-0 w-6 h-6 mt-0.5 rounded-[4px] border-2 cursor-pointer flex items-center justify-center transition-colors"
              [style.background-color]="confirmChecked ? '#27AE60' : '#ffffff'"
              [style.border-color]="confirmChecked ? '#27AE60' : '#9CA3AF'"
            >
              @if (confirmChecked) {
                <svg width="14" height="11" viewBox="0 0 14 11" fill="none">
                  <path d="M1 5L5 9L13 1" stroke="white" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              }
            </div>
            <span
              (click)="confirmChecked = !confirmChecked"
              class="text-[20px] text-heading cursor-pointer leading-snug"
            >
              To confirm deletion, check the box and tap Delete to continue.
            </span>
          </div>
        </div>

        <ng-template pTemplate="footer">
          <div class="flex items-center justify-between gap-3 pt-1">
            <button
              (click)="close()"
              class="text-[20px] font-semibold text-heading bg-transparent border-none cursor-pointer px-2 hover:text-gray-700 transition-colors"
            >
              Cancel
            </button>
            <button
              (click)="onDeleteAccount()"
              [disabled]="!confirmChecked || loading()"
              class="text-white border-none rounded-md px-5 py-2.5 text-sm font-semibold cursor-pointer transition-colors"
              [class]="(confirmChecked && !loading())
                ? 'bg-[#C34137] '
                : 'bg-[#8C929A] cursor-not-allowed'"
            >
              {{ loading() ? 'Deleting...' : 'Delete' }}
            </button>
          </div>
        </ng-template>
      </p-dialog>
    }

    <!-- ── Step 4: Success ───────────────────────────────────────────────── -->
    @if (step() === 'success') {
      <p-dialog
        [visible]="true"
        [modal]="true"
        [closable]="false"
        [draggable]="false"
        [resizable]="false"
        styleClass="pra-deletion-dialog pra-success-dialog"
      >
        <div class="flex flex-col items-center text-center px-[24px] pt-[44px] pb-[36px]">
          <div class="w-[64px] h-[64px] rounded-full bg-[#2AACA8] flex items-center justify-center mb-5 shrink-0">
            <i class="pi pi-check text-white !text-[28px] !font-bold"></i>
          </div>
          <h2 class="text-[24px] font-bold text-heading mb-2 mt-0">Account Deleted</h2>
          <p class="text-base text-[#4E4D4D] leading-6 m-0 mb-8">
            Your account has been successfully deleted. Thank you for your time with us.
          </p>
          <button
            type="button"
            (click)="onSuccessClose()"
            class="w-full bg-[#192A3E] text-white border-none rounded-[8px] py-3 text-base font-semibold cursor-pointer hover:opacity-90 transition-opacity"
          >
            OK
          </button>
        </div>
      </p-dialog>
    }
